// The `HighlightCategorySection` component is a React functional component designed to display a
// carousel of category tiles. It supports infinite scrolling by cloning slides and managing their
// indices for smooth transitions. The component adjusts the number of visible slides dynamically
// based on screen size using media queries and provides navigation controls for scrolling left or right.
// The slides are rendered with customizable widths and snap alignment for a clean, responsive layout.

import React, { useMemo, useRef, useState, useEffect } from 'react';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop, tablet, mobile, ultraDesktop } from 'helpers/utils/screensizes';
import Wrapper from 'components/HOC/wrapper';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Carousel, CarouselContentItem } from 'types/next';
import CategoryTile from './tile';
import { SwiperOptions } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import useLocale from 'helpers/hooks/useLocale';
export type CategoryData = {
  content: Carousel | undefined;
};

const HighlightCategorySection = ({ content }: CategoryData) => {
  const { isTouchDevice } = useTouchDevice();
  const contentItems = (content?.contentItems as CarouselContentItem[]) || [];
  const swiperRef = useRef<any>(null); // Store Swiper instance
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [isultraDesktop] = useMediaQuery(ultraDesktop);
  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const [ismobile] = useMediaQuery(mobile);
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';

  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const slidesPerView = useMemo(() => {
    if (isultraDesktop) return content?.slidesPerView?.largeDesktop || 3.5;
    if (isMediumDesktopSize) return content?.slidesPerView?.largeDesktop || 3.5;
    if (isDesktopSize) return content?.slidesPerView?.desktop || 2.5;
    if (isTablet) return content?.slidesPerView?.tablet || 2.5;
    return content?.slidesPerView?.mobile || 1.5;
  }, [isultraDesktop, isMediumDesktopSize, isDesktopSize, isTablet]);
  const loopedSlides = Math.ceil(slidesPerView) || 1;

  // Fix Me : Need to confirm the UI
  // const visibleSlides = Math.floor(slidesPerView);
  // const slideWidth = useMemo(() => {
  //   if (scrollContainerRef.current) {
  //     return scrollContainerRef.current.offsetWidth / slidesPerView;
  //   }
  //   return 0;
  // }, [slidesPerView]);

  // // Clone slides for infinite scrolling
  // const loopedSlides = useMemo(() => {
  //   return [...contentItems, ...contentItems.slice(0, visibleSlides)];
  // }, [contentItems, visibleSlides]);

  // useEffect(() => {
  //   if (startIndex === -1) {
  //     setTimeout(() => {
  //       setStartIndex(contentItems.length - 1);
  //       scrollContainerRef.current?.scrollTo({
  //         left: (contentItems.length - 1) * slideWidth,
  //         behavior: 'auto',
  //       });
  //     }, 300);
  //   } else if (startIndex === contentItems.length) {
  //     setTimeout(() => {
  //       setStartIndex(0);
  //       scrollContainerRef.current?.scrollTo({
  //         left: 0,
  //         behavior: 'auto',
  //       });
  //     }, 300);
  //   }
  // }, [startIndex, contentItems.length, slideWidth]);

  // const handleScrollLeft = () => {
  //   setStartIndex((prev) => (prev <= -1 ? contentItems.length - 1 : prev - 1));
  //   scrollContainerRef.current?.scrollBy({
  //     left: -slideWidth,
  //     behavior: 'smooth',
  //   });
  // };

  // const handleScrollRight = () => {
  //   setStartIndex((prev) => (prev >= contentItems.length ? 0 : prev + 1));
  //   scrollContainerRef.current?.scrollBy({
  //     left: slideWidth,
  //     behavior: 'smooth',
  //   });
  // };

  // const handleLeftClick = () => {
  //   setStartIndex((prevIndex) => (prevIndex - 1 + contentItems.length) % contentItems.length);
  // };

  // const handleRightClick = () => {
  //   setStartIndex((prevIndex) => (prevIndex + 1) % contentItems.length);
  // };
  // const handleLeftClick = () => {
  //   if (swiperRef.current) swiperRef.current.slidePrev();
  // };

  // const handleRightClick = () => {
  //   if (swiperRef.current) swiperRef.current.slideNext();
  // };

  useEffect(() => {
    if (scrollContainerRef.current) {
      setSlideWidth(scrollContainerRef.current.offsetWidth / slidesPerView);
    }
  }, [slidesPerView]);

  // Scroll event handler to track scrollbar position
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

    setIsScrolledToStart(isAtStart);
    setIsScrolledToEnd(isAtEnd);

    const newStartIndex = Math.round(scrollLeft / slideWidth);
    setStartIndex(newStartIndex);
  };

  // Scroll Left with Smooth Behavior
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Scroll Right with Smooth Behavior
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Attach scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Run initially to set correct button states
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [contentItems?.length, slidesPerView, slideWidth]);

  useEffect(() => {
    if (scrollContainerRef.current && slideWidth > 0) {
      scrollContainerRef.current.scrollTo({
        left: 0,

        behavior: 'auto',
      });
      setStartIndex(0);
    }
  }, [slideWidth]);

  const renderItems = (items: CarouselContentItem[]) => {
    if (items.length === 0) return null;

    const loopedItems = [...items, ...items, ...items];
    const start = items.length + startIndex;
    const end = start + Math.ceil(slidesPerView);

    return loopedItems.slice(start, end).map((item, index) => (
      <div
        key={index}
        style={{
          flex: `0 0 calc(100% / ${slidesPerView})`,
          scrollSnapAlign: 'start',
        }}
      >
        <CategoryTile data={item} locale={selectedLocale} />
      </div>
    ));
  };

  return (
    <Wrapper clearDefaultStyles className="homepage-carousels-section pb-16 pl-16">
      <div className={`mb-16 flex items-center ${isDesktopSize ? 'relative justify-center' : 'justify-between pr-10'}`}>
        <div className="flex w-full justify-start lg:justify-center lg:text-center">
          <h2 className="font-body text-24 font-bold uppercase leading-9 sm:text-36">
            {content?.heading?.[selectedLocale] ?? ''}
          </h2>
        </div>
        {(content?.contentItems?.length ?? 0) > slidesPerView && (
          <div className={`flex items-center justify-end gap-x-6 ${isDesktopSize ? 'absolute right-[1rem]' : ''}`}>
            <button
              className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0] 
                ${isScrolledToStart ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={handleScrollLeft}
              disabled={isScrolledToStart}
              aria-disabled={isScrolledToStart}
            >
              <ChevronLeftIcon
                className="mt-2 h-20 w-20 font-bold text-[#404040]"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
            </button>
            <button
              className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0] 
    ${isScrolledToEnd ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={handleScrollRight}
              disabled={isScrolledToEnd}
              aria-disabled={isScrolledToEnd}
            >
              <ChevronRightIcon
                className="mt-2 h-20 w-20 font-bold text-[#404040]"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
            </button>
          </div>
        )}
      </div>
      {/* // Fix Me : Need to confirm the UI */}
      {/* <div
        ref={scrollContainerRef}
        className="flex overflow-hidden"
        style={{
          gap: isTablet ? '10px' : '5px',
        }}
      >
        {loopedSlides.map((data, index) => (
          <div
            key={index}
            style={{
              flex: `0 0 calc(89% / ${slidesPerView})`,
              scrollSnapAlign: 'start',
            }}
          >
            <CategoryTile data={data} />
          </div>
        ))}
      </div> */}
      {/* <Slider
        slidesPerView={slidesPerView}
        slidesPerGroup={1}
        dots={false}
        arrows={false} // Disable default Swiper arrows
        loop={true} // Enable infinite loop
        loopedSlides={loopedSlides}
        onSwiper={(swiper) => (swiperRef.current = swiper)} // Store Swiper instance
        allowTouchMove
        spaceBetween={8}
        // breakpoints={{
        //   [tablet]: {
        //     slidesPerView,
        //     spaceBetween: 15,
        //   },
        //   [desktop]: {
        //     slidesPerView,
        //     spaceBetween: 15,
        //   },
        //   [mediumDesktop]: {
        //     slidesPerView,
        //     spaceBetween: 15,
        //   },
        //   [ultraDesktop]: {
        //     slidesPerView,
        //     spaceBetween: 15,
        //   },
        // }}
        // {...props}
      >
        {contentItems?.map((item, index) => (
          <CategoryTile data={item} locale={selectedLocale} key={index} />
        ))}
      </Slider> */}
      <div
        ref={scrollContainerRef}
        className="ag__cc__wrapper scroll-snap-x scroll-snap-mandatory flex overflow-x-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          paddingBottom: '10px', // reserve scrollbar height
        }}
      >
        {contentItems?.map((data, index) => (
          <div
            key={index}
            style={{
              flex: `0 0 calc(100% / ${slidesPerView})`,
              scrollSnapAlign: 'start',
            }}
          >
            <CategoryTile data={data} locale={selectedLocale} />
          </div>
        ))}
      </div>
      {/* <div className={`flex items-stretch justify-around gap-x-15 overflow-hidden`}>{renderItems(contentItems)}</div> */}
    </Wrapper>
  );
};

export default HighlightCategorySection;
